import { useState, useEffect, useCallback } from 'react';
import { Idea } from '~/content/types';
import { gql, useLazyQuery } from '@apollo/client';
import { useAccount } from '~/accountContext';
import { useDispatch } from 'react-redux';
import { ideasReceived, selectAllIdeas } from '~/content/state/ideasSlice';
import { useAppSelector } from '~/store';

export const QUERY_IDEAS = gql`
  query ($input: IdeasListInput!) {
    ideas(input: $input) {
      ... on PaginatedIdeasList {
        edges {
          node {
            id
            content {
              text
              media {
                id
                url
                alt
                thumbnailUrl
                type
                size
                source {
                  name
                  id
                }
              }
            }
            organizationId
            createdAt
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

function useIdeasLoader(): {
  ideas: Idea[];
  loading: boolean;
  error: null | string;
  loadMore: () => void;
  handleScroll: (e: any) => void;
} {
  const dispatch = useDispatch();
  const numIdeas = 25;
  const [pageInfo, setPageInfo] = useState<{
    hasNextPage?: boolean;
    endCursor?: string;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { account } = useAccount() as any;
  const organizationId = account?.currentOrganization?.id;

  const [getIdeas, { data, loading: queryLoading, fetchMore }] = useLazyQuery(
    QUERY_IDEAS,
    {}
  );

  function onFetchComplete(res) {
    const pageInfoRes = res.ideas?.pageInfo || {};
    setPageInfo(pageInfoRes);

    const resIdeas = res.ideas?.edges || [];
    dispatch(ideasReceived(resIdeas.map((i) => i.node)));
    setLoading(false);
  }

  useEffect(() => {
    if (account?.currentOrganization?.id) {
      setLoading(true);
      getIdeas({
        variables: {
          input: {
            organizationId: `${organizationId}`,
            first: numIdeas,
            order: {
              by: 'createdAt',
              value: 'DESC',
            },
          },
        },
        onCompleted: onFetchComplete,
      });
    }
  }, [account]);

  let debounceTimer;
  function debounce(callback, time) {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(callback, time);
  }

  const loadMore = useCallback(() => {
    if (pageInfo.hasNextPage && !queryLoading) {
      debounce(() => {
        fetchMore({
          variables: {
            input: {
              organizationId: `${organizationId}`,
              first: numIdeas,
              after: pageInfo.endCursor,
              order: {
                by: 'createdAt',
                value: 'DESC',
              },
            },
          },
        });
      }, 250);
    }
  }, [pageInfo]);

  function handleScroll({ target }: React.ChangeEvent<HTMLInputElement>): void {
    const offset = 5;
    const position = Math.floor(target.scrollHeight - target.scrollTop);
    const bottom = position <= target.clientHeight + 32;
    if (bottom) loadMore();
  }

  const ideas = useAppSelector(selectAllIdeas);

  return {
    error,
    handleScroll,
    ideas,
    loadMore,
    loading,
  };
}

export { useIdeasLoader };
